import type { Atom } from 'jotai';
import { getDefaultStore } from 'jotai';

type WaitForOptions = {
  store?: ReturnType<typeof getDefaultStore>;
  /** Reject if the condition doesn't become true in time */
  timeoutMs?: number;
  signal?: AbortSignal;
};

/**
 * Wait until `predicate(value)` is true for the given atom.
 * Resolves with the matching value. Cleans up subscriptions automatically.
 */
export function waitForAtom<T>(
  atom: Atom<T>,
  predicate: (value: T) => boolean,
  opts: WaitForOptions = {}
): Promise<T> {
  const store = opts.store ?? getDefaultStore();

  return new Promise<T>((resolve, reject) => {
    if (opts.signal?.aborted) {
      const error = new Error('waitForAtom was aborted');
      error.name = 'AbortError';
      reject(error);
      return;
    }

    const initialValue = store.get(atom);
    if (predicate(initialValue)) {
      resolve(initialValue);
      return;
    }

    const cleanup = () => {
      unsub();
      if (timeoutId) clearTimeout(timeoutId);
      opts.signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = () => {
      cleanup();
      const error = new Error('waitForAtom was aborted');
      error.name = 'AbortError';
      reject(error);
    };
    const unsub = store.sub(atom, () => {
      const value = store.get(atom);
      if (predicate(value)) {
        cleanup();
        resolve(value);
      }
    });
    const timeoutId = opts.timeoutMs
      ? setTimeout(() => {
          cleanup();
          reject(new Error(`[TIMEOUT] waitForAtom timed out after ${opts.timeoutMs}ms`));
        }, opts.timeoutMs)
      : null;

    opts.signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export { atomWithHash } from './atomWithHash';
export type { AtomWithHashOptions } from './atomWithHash';
